import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Siren, Edit, Trash2, Plus, X, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const PriorityList = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ 1. INITIALIZE STATE FROM SESSION STORAGE
  // We use unique keys ('priority_search', etc.)
  const [searchTerm, setSearchTerm] = useState(() => {
      return sessionStorage.getItem('priority_search') || '';
  });

  const [entriesPerPage, setEntriesPerPage] = useState(() => {
      return parseInt(sessionStorage.getItem('priority_entries')) || 10;
  });

  const [currentPage, setCurrentPage] = useState(() => {
      return parseInt(sessionStorage.getItem('priority_page')) || 1;
  });

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Edit/Delete States
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);

  // Form Data
  const [formData, setFormData] = useState({
    full_name: '', phone_number: '', dob: '', address: '', 
    insurance_provider: '', insurance_policy_no: '', status: 'Active'
  });

  // Change this to match your local folder path
const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';
  useEffect(() => {
    fetchPriorityPatients();
  }, []);

  // ✅ 2. SAVE STATE WHENEVER IT CHANGES
  useEffect(() => {
    sessionStorage.setItem('priority_search', searchTerm);
    sessionStorage.setItem('priority_entries', entriesPerPage);
    sessionStorage.setItem('priority_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  // Auto-Scroll Logic
  useEffect(() => {
    if (!loading && patients.length > 0) {
        const lastViewedId = sessionStorage.getItem('lastViewedPriorityId');
        if (lastViewedId) {
            setTimeout(() => {
                const element = document.getElementById(`priority-row-${lastViewedId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.classList.add('bg-teal-50');
                    setTimeout(() => element.classList.remove('bg-teal-50'), 2000);
                }
            }, 100);
            sessionStorage.removeItem('lastViewedPriorityId');
        }
    }
  }, [loading, patients]);

  const fetchPriorityPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_priority_patients`);
      if (Array.isArray(response.data)) setPatients(response.data);
      setLoading(false);
    } catch (error) { console.error(error); setLoading(false); }
  };

  const filteredPatients = patients.filter((p) => {
    const search = searchTerm.toLowerCase();
    return (p.full_name || '').toLowerCase().includes(search) || String(p.id).includes(search);
  });

  // ✅ Pagination Logic
  const totalEntries = filteredPatients.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  // ✅ 3. RESET PAGE LOGIC
  // If current page is greater than total pages (e.g., after filtering), reset to 1
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
    }
  }, [searchTerm, totalPages]);

  // --- HANDLERS ---

  // ✅ NEW: Handle Search Change (Resets Page)
  const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
      setCurrentPage(1); 
  };

  const handleViewDetails = (patientId) => {
      sessionStorage.setItem('lastViewedPriorityId', patientId);
      navigate(`/priority/${patientId}`);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Open Add Modal
  const openAddModal = () => {
    setIsEditing(false);
    setEditId(null);
    setFormData({ full_name: '', phone_number: '', dob: '', address: '', insurance_provider: '', insurance_policy_no: '', status: 'Active' });
    setShowModal(true);
  };

  // Open Edit Modal
  const handleEditClick = (e, patient) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditId(patient.id);
    setFormData({
        full_name: patient.full_name,
        phone_number: patient.phone_number,
        dob: patient.dob,
        address: patient.address || '',
        insurance_provider: patient.insurance_provider,
        insurance_policy_no: patient.insurance_policy_no || '', 
        status: patient.status || 'Active'
    });
    setShowModal(true);
  };

  // Open Delete Modal
  const confirmDeleteClick = (e, patient) => {
    e.stopPropagation();
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  // Execute Delete
  const executeDelete = async () => {
    if (!patientToDelete) return;
    try {
        await axios.post(`${API_URL}?action=delete_patient`, { id: patientToDelete.id });
        setShowDeleteModal(false);
        fetchPriorityPatients();
    } catch (error) { console.error(error); alert("Error deleting"); }
  };

  // Submit Form (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = isEditing ? 'update_patient' : 'add_patient';
    const payload = isEditing ? { ...formData, id: editId } : formData;

    try {
        await axios.post(`${API_URL}?action=${action}`, payload);
        setShowModal(false);
        fetchPriorityPatients();
    } catch (error) { console.error(error); alert("Error saving"); }
  };

  const formatID = (id) => String(id).padStart(4, '0'); 
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    const cleanDate = dateString.split(' ')[0].split('T')[0];
    const [year, month, day] = cleanDate.split('-');
    if(year && month && day) return `${month}/${day}/${year}`;
    return dateString;
  };

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-3">
            <Siren className="text-teal-600" size={30} /> Priority Call List
        </h2>
        
        <div className="flex gap-3">
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search Patient..." 
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-teal-500" 
                    value={searchTerm} 
                    onChange={handleSearchChange} // ✅ Updated Handler
                />
            </div>
            
            <button onClick={openAddModal} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 shadow">
                <Plus size={16} /> Register
            </button>
        </div>
      </div>

      {/* ✅ Entries Dropdown */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select 
            value={entriesPerPage} 
            onChange={(e) => {
                setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                setCurrentPage(1); // Reset page when changing entries
            }}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
        </div>
      </div>

      {/* MAIN TABLE */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Patients Name</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Address</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Phone Number</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">DOB</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Insurance</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">RX Count</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="9" className="text-center py-4">Loading...</td></tr>
            ) : paginatedPatients.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-10 text-gray-400 italic">No priority patients (F, B, U) found.</td></tr>
            ) : (
              paginatedPatients.map((p) => (
                <tr 
                    key={p.id} 
                    id={`priority-row-${p.id}`} 
                    className="hover:bg-red-50 transition cursor-pointer" 
                    onClick={() => handleViewDetails(p.id)}
                >
                    <td className="px-4 py-3 font-mono text-gray-500">{formatID(p.id)}</td>
                    <td className="px-4 py-3"><span className="text-blue-600 font-bold hover:underline">{p.full_name}</span></td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={p.address}>{p.address || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{p.phone_number}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDate(p.dob)}</td>
                    <td className="px-4 py-3 text-gray-700">{p.insurance_provider}</td>
                    <td className="px-4 py-3 text-center">
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">
                            {p.rx_count} Active
                        </span>
                    </td>
                    
                    <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold border ${
                            p.priority_status === 'U' ? 'bg-red-100 text-red-800 border-red-200' :
                            p.priority_status === 'B' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                            p.priority_status === 'F' ? 'bg-green-100 text-green-800 border-green-200' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {p.priority_status || p.status}
                        </span>
                    </td>
                    
                    <td className="px-4 py-3 text-center flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button onClick={(e) => handleEditClick(e, p)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded"><Edit size={16}/></button>
                        <button onClick={(e) => confirmDeleteClick(e, p)} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={16}/></button>
                    </td>
                </tr>
            ))
          )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination Controls */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          
          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (
                pageNum === 1 || 
                pageNum === totalPages || 
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 border rounded ${
                      currentPage === pageNum 
                        ? 'bg-teal-600 text-white border-teal-600' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}
          </div>

          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-1/3 p-6 relative">
                <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    {isEditing ? <Edit className="text-yellow-500"/> : <Plus className="text-teal-600" />} 
                    {isEditing ? 'Edit Patient Details' : 'New Patient Registration'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700">Full Name</label><input type="text" name="full_name" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.full_name} onChange={handleInputChange}/></div><div><label className="block text-sm font-medium text-gray-700">Status</label><select name="status" className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white" value={formData.status} onChange={handleInputChange}><option value="F">F</option><option value="B">B</option><option value="U">U</option><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Home Address</label><input type="text" name="address" placeholder="Street, City, Zip" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.address} onChange={handleInputChange}/></div>
                    <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700">Phone Number</label><input type="text" name="phone_number" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.phone_number} onChange={handleInputChange}/></div><div><label className="block text-sm font-medium text-gray-700">Date of Birth</label><input type="date" name="dob" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.dob} onChange={handleInputChange}/></div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Insurance Provider</label><input type="text" name="insurance_provider" placeholder="e.g. BlueCross" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.insurance_provider} onChange={handleInputChange}/></div>
                    
                    <button type="submit" className={`w-full text-white font-bold py-2 rounded transition mt-4 ${isEditing ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-teal-600 hover:bg-teal-700'}`}>{isEditing ? 'Update Patient' : 'Register Patient'}</button>
                </form>
            </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
                <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500 mt-2">Delete <strong>{patientToDelete.full_name}</strong>?</p>
                <div className="mt-6 flex justify-center gap-3"><button onClick={() => setShowDeleteModal(false)} className="border px-4 py-2 rounded">Cancel</button><button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded">Delete</button></div>
            </div>
        </div>
      )}

    </div>
  );
};

export default PriorityList;